import { Link } from '../../../components/Link';
import {
  Lead,
  LearnCode,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('email-bounce-rate');

/** PILLAR of the signup-integrity cluster. */
export default function EmailBounceRatePage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'Bounce rate is the share of sent messages a receiving server refused. Hard bounces mean the address does not exist and are permanent; soft bounces are temporary and resolve on retry. Only the hard rate matters for reputation, and above roughly 2% providers start treating you as someone who does not maintain a list.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="Hard and soft are not degrees of the same thing">
        <p>
          They are separate problems with separate causes and separate remedies, and averaging them
          into one percentage is how people end up fixing the wrong one.
        </p>
        <LearnTable
          head={['', 'Hard bounce', 'Soft bounce']}
          rows={[
            ['Means', 'The address does not exist', 'The address exists but could not accept right now'],
            ['SMTP class', <Tok key="a">5xx</Tok>, <Tok key="b">4xx</Tok>],
            ['Typical', '550 5.1.1 user unknown', '452 4.2.2 mailbox full'],
            ['Retry?', <Lead key="c">Never</Lead>, 'Yes, with backoff'],
            ['Counts against reputation', <Lead key="d">Heavily</Lead>, 'Barely, unless persistent'],
            ['Action', 'Suppress on the first occurrence', 'Retry, then suppress after repeated failure'],
          ]}
        />
        <p>
          The distinction is carried in the response itself — the first digit of the SMTP reply and
          the enhanced status code beside it. Any sending platform worth using classifies on that
          rather than on the human-readable text, and{' '}
          <Link to="/learn/smtp-error-codes" className="text-accent underline underline-offset-4">
            the codes decode cleanly
          </Link>{' '}
          once you know which is which.
        </p>
      </LearnSection>

      <LearnSection title="Where the thresholds actually sit">
        <p>
          Providers publish complaint-rate ceilings far more readily than bounce ceilings, so the
          numbers below are a mix of stated policy and consistently reported ESP enforcement. Treat
          the first row as a hard fact and the rest as operating guidance.
        </p>
        <LearnTable
          head={['Rate', 'Reading', 'What happens']}
          rows={[
            ['Under 0.5%', <Lead key="a">Healthy</Lead>, 'A well-maintained list on confirmed signups'],
            ['0.5% – 2%', 'Acceptable', 'Normal drift as addresses age out. Clean periodically'],
            ['2% – 5%', 'Flagged', 'ESPs warn; providers begin discounting your sending'],
            ['Above 5%', <Lead key="b">At risk</Lead>, 'Account suspension is a realistic outcome at most ESPs'],
            ['Above 10%', 'Bought or scraped', 'Rebuild the list. There is no cleaning route back'],
          ]}
        />
        <p>
          Two things worth separating from the number. Gmail states a <em>complaint</em> ceiling of
          0.3%, which is a different metric and a much smaller budget — see{' '}
          <Link to="/learn/sender-reputation" className="text-accent underline underline-offset-4">
            sender reputation
          </Link>
          . And a low bounce rate on a large old list can hide a spam-trap problem, because a
          recycled trap is a real, deliverable mailbox. It never bounces.
        </p>
      </LearnSection>

      <LearnSection title="Why the rate matters more than the bounces">
        <p>
          A hard bounce costs you nothing on its own. Addresses die, people leave companies, domains
          lapse. What a provider reads is the <em>rate</em>, because it answers a question about you
          rather than about the address:
        </p>
        <LearnCode>
          {`0.3% hard bounces  → "this sender removes dead addresses"
6.0% hard bounces  → "this sender is mailing a list they did not build"`}
        </LearnCode>
        <p>
          That inference is the whole mechanism. Someone mailing addresses they collected
          themselves, with consent, has few dead ones. Someone mailing a purchased file has many.
          The rate distinguishes them without the provider needing to know anything else, which is
          exactly why it is weighted.
        </p>
      </LearnSection>

      <LearnSection title="What actually drives it up">
        <LearnList>
          <li>
            <Lead>Purchased or scraped lists.</Lead> The largest cause by a distance, and the only
            one with no remedy short of not sending to it. Also carries{' '}
            <Link to="/learn/why-email-goes-to-spam" className="text-accent underline underline-offset-4">
              spam traps
            </Link>
            , which are worse than bounces because they never announce themselves.
          </li>
          <li>
            <Lead>Typos at signup.</Lead> <Tok>gmial.com</Tok>, <Tok>hotmial.com</Tok>,{' '}
            <Tok>yaho.com</Tok>. Entirely preventable at the point of entry and completely
            unfixable afterwards.
          </li>
          <li>
            <Lead>Natural decay.</Lead> Roughly 2% of a B2B list goes stale per month as people
            change jobs. A list untouched for a year is substantially dead through no fault of
            anyone.
          </li>
          <li>
            <Lead>Sending to </Lead>
            <Link to="/learn/catch-all-email" className="text-accent underline underline-offset-4">
              catch-all domains
            </Link>
            . These accept everything at SMTP time and bounce later, or silently discard. Your
            bounce rate looks fine while the mail goes nowhere.
          </li>
          <li>
            <Lead>Not suppressing.</Lead> Re-sending to an address that already hard-bounced is the
            avoidable one, and it compounds: the same dead address inflates every send.
          </li>
        </LearnList>
      </LearnSection>

      <LearnSection title="Getting it down">
        <LearnSubsection title="At the point of entry — where it is cheap">
          <p>
            Validating at signup costs one API call and prevents the address ever entering the
            list. Syntax, then whether the domain has MX records at all, then whether the mailbox
            accepts mail. Catching a typo here is worth more than any amount of cleaning later,
            because the address never contributes to a rate.
          </p>
        </LearnSubsection>
        <LearnSubsection title="On the existing list — before the next big send">
          <p>
            Verify the list, suppress the confirmed-dead, and segment the unknowns rather than
            treating them as good. Do this before a campaign rather than after: the send is what
            converts a bad list into a reputation problem.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Continuously — the part people skip">
          <p>
            Suppress on the first hard bounce, automatically, with no manual step. Then re-verify
            periodically, because a list decays whether or not you mail it. Quarterly is reasonable
            for B2B; annually is not.
          </p>
        </LearnSubsection>
        <LearnSubsection title="What not to do">
          <p>
            Do not send a re-engagement campaign to a list you suspect is dead. That is a large send
            to unverified addresses, which is the exact shape of the problem you are trying to
            solve. Verify first, then re-engage the survivors.
          </p>
        </LearnSubsection>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'What is a good email bounce rate?',
    a: 'Under 0.5% for a list built from confirmed signups. Up to 2% is acceptable drift. Above 2% ESPs start flagging you, above 5% suspension becomes realistic, and above 10% the list was almost certainly bought rather than built.',
  },
  {
    q: 'What is the difference between a hard and a soft bounce?',
    a: 'A hard bounce is a permanent 5xx refusal — the address does not exist, and it should be suppressed on the first occurrence. A soft bounce is a temporary 4xx, such as a full mailbox, and it usually resolves on retry. Only the hard rate meaningfully affects reputation.',
  },
  {
    q: 'Does one hard bounce hurt my reputation?',
    a: 'No. Individual bounces are normal and expected — addresses die constantly. What providers read is the rate, because it tells them whether you maintain your list. A single bounce says nothing; six percent says you are mailing a list you did not build.',
  },
  {
    q: 'Why did my bounce rate spike suddenly?',
    a: 'Usually a new list segment entering the send, an import that was never verified, or a suppression list that stopped being applied. Compare the bouncing addresses against your most recent import date — the pattern is normally obvious once you group by acquisition source.',
  },
  {
    q: 'Can I lower it by removing bounced addresses after the fact?',
    a: 'Only for future sends, which is the point. Suppressing is what stops the same dead address inflating every subsequent campaign. It does not undo the damage from sends that already happened; that recovers through weeks of clean sending.',
  },
  {
    q: 'Do catch-all domains show up as bounces?',
    a: 'Usually not, and that is the problem. A catch-all accepts everything at SMTP time, so a non-existent address there either bounces much later or is silently discarded. Your bounce rate looks healthy while the mail reaches nobody.',
  },
];
